import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { appSettings } from 'app/appSettings';
import { ContractDetailsRestService } from 'app/core/services/rest/contract-details.rest.service';
import * as actions from 'app/core/store/customer-info/customer-info.actions';
import { Customer } from 'app/core/store/session/user-context/user-context.interface';
import { Observable, of, Subject } from 'rxjs';
import * as fromRoot from '../store';
import { goAction } from '../store/router/router.actions';
import { debounceTime, filter, first, withLatestFrom } from 'rxjs/operators';
import { logger } from '../util/logger';
import { getFirst$$ } from '../util/util';
import { AzureMonitoringService } from './AzureMonitoringService';

@Injectable({ providedIn: 'root' })
export class CustomerInfoService {
  private customer: Customer;

  constructor(private store: Store<fromRoot.RootState>, private contractDetailsRestService: ContractDetailsRestService, private router: Router, private azureMonitoringService: AzureMonitoringService,) {
  }

  resetCustomerInfo(): void {
    this.store
      .select(state => state)
      .pipe(first())
      .subscribe((state: fromRoot.RootState) => {
        const isSuperUser = state.session.userContext.isSuperUser;
        if (!isSuperUser) {
          this.store.dispatch(new actions.CustomerInfoResetAction());
        }
      });
  }

  loadCustomerInfo(customer?: Customer, forceLoad = false): Observable<void> {
    logger.info(`In loadCustomerInfo for customer ${customer?.name}`);
    this.customer = customer;
    const subject = new Subject<void>()
    // First make sure that definitions are loaded !!! (needed for Definitions of orderTYpes
    // definitions are fetched by the definitions guard

    // Then start loading customerInfo
    this.store.select(state => ({ userContext: state.session.userContext, selectedCustomer: state.session.selectedCustomer, definitions: state.definitions }))
      .pipe(
        filter(({ definitions }) => definitions.loaded),
        debounceTime(1), // make it async so that observable is returned before completed
        first()
      )
      .subscribe(({ definitions, selectedCustomer, userContext }) => {
        // this function is executed after the user context api call (only need isSuperUser from the context call)
        // so doesn't matter if code after the function call resolves later
        const isSuperUser = userContext.isSuperUser;

        if (userContext.contextLoaded && (forceLoad || !isSuperUser)) {
          // eslint-disable-next-line
          customer = customer || (selectedCustomer.loadSuccess && selectedCustomer.data);
          if (customer) {
            this.store.dispatch(new actions.CustomerInfoLoadAction({ customer }));
            this.contractDetailsRestService.getCustomerInfo(customer.id)
              .pipe(
                withLatestFrom(this.store.select(state => state.definitions)) // take latest definitions (other language will be loaded by now)
              )
              .subscribe({
                next: ([customerInfo, definitions]) => {
                  this.store.dispatch(
                    new actions.CustomerInfoLoadSuccessAction({
                      customerInfo,
                      customer,
                      isAdmin: userContext.isAdmin,
                      isAgent: userContext.isAgent,
                      isEpsUser: userContext.isEpsUser,
                      canRelocate: userContext.canRelocate,
                      useEmailActors: userContext.useEmailActors,
                      canAccessHomeMarketBonus: userContext.canAccessHomeMarketBonus,
                      isSuperUser: userContext.isSuperUser,
                      definitions,
                      adminRoles: userContext.adminRoles,
                    })
                  );

                  if (this.router.url === '/' + appSettings.SELECT_CUSTOMER_ROUTE) {
                    this.store.dispatch(goAction({ path: appSettings.DEFAULT_ROUTE }));
                  }

                  subject.next();
                  subject.complete();
                },
                error: error => {
                  this.azureMonitoringService.logException(error, { message: 'getCustomerInfo fail' });
                  this.store.dispatch(new actions.CustomerInfoLoadFailAction(error));
                  subject.error(error);
                },
              });
          } else {
            this.azureMonitoringService.logException(new Error('getCustomerInfo: no customer'));
            this.store.dispatch(new actions.CustomerInfoLoadFailAction(null));
            subject.next();
            subject.complete();
          }
        } else {
          subject.next();
          subject.complete();
        }
      });

    return subject.asObservable();
  }
}
